import { useEffect, ReactElement, useState } from 'react';
import { useAuth, AuthContextInterface } from '@guildeducationinc/guild-auth';
import { useMediaQuery } from 'react-responsive';
import { DialogButton, Typography, RadioButtons } from '@guildeducationinc/recess';
import { AxiosResponse } from 'axios';
import { ModalOptions, useModal } from '@src/context/ModalContext';
import { useEnrollmentsData } from '@src/context/EnrollmentsContext';
import { Application } from '@src/hooks/useApplications';
import { MOBILE_QUERY } from '@src/constants';
import { clickable } from '@src/analytics';
import { useEnrollmentService, EnrollmentIntentions } from '@src/api/enrollmentServiceClient';
import EnrollmentSuccessModal from '@src/components/Enrollment/EnrollmentSuccessModal/EnrollmentSuccessModal';
import './EnrollmentConfirmationModal.scss';

export interface onSubmitAction {
  applicationId: string;
  enrollmentUuid: string;
  enrollmentIntent: EnrollmentIntentions;
}

export interface EnrollmentModalProps {
  state?: Partial<AuthContextInterface>;
  application: Application;
  onSubmit: (action: onSubmitAction) => void;
}

const EnrollmentConfirmationModal = ({
  application,
  onSubmit,
}: EnrollmentModalProps): ReactElement => {
  const isMobile = useMediaQuery(MOBILE_QUERY);
  const { setModalOptions, closeModal, openModal } = useModal();
  const { enrollmentsRefetch } = useEnrollmentsData();
  const [enrollmentIntent, setEnrollmentIntent] = useState<string>();
  const [errorMessage, setErrorMessage] = useState<string | undefined>(undefined);
  const { user, getAccessToken } = useAuth();
  const { post } = useEnrollmentService(getAccessToken);
  const apName = application?.program?.studentFacingAcademicPartner?.name;
  const [currentEnrollment] = application?.enrollmentIntentions ?? [];
  const formattedStartDate = new Date(currentEnrollment?.term?.startDate || '').toLocaleDateString(
    'en-us',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      timeZone: 'UTC',
    }
  );

  const newTermFormattedStartDate = new Date(
    currentEnrollment?.newTerm?.startDate || ''
  ).toLocaleDateString('en-us', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    timeZone: 'UTC',
  });

  const logo = application?.program?.studentFacingAcademicPartner?.logos?.color;
  const isExpired = currentEnrollment.isExpired;
  const rolloverConditions = isExpired && currentEnrollment.newTerm;

  const enrollmentClickable = () => {
    clickable({
      loggedIn: true,
      product: 'home',
      tag: 'home_enrollment_modal',
      text: 'Submit',
      type: 'link',
    });
  };

  const handleSubmit = (enrollmentIntent: string | undefined) => {
    if (enrollmentIntent === undefined) {
      return setErrorMessage('You must accept or decline your enrollment.');
    }

    setErrorMessage(undefined);

    handleIntent(enrollmentIntent === 'true');
    enrollmentClickable();
  };

  interface EnrollmentServiceResponse extends AxiosResponse {
    intends_to_enroll: boolean;
    max_enrollments_allowed: number;
    recorded_at: string;
    is_expired: boolean;
    term: {
      code: string;
      name: string;
      start_date: string;
      end_date: string;
    };
    enrollment_uuid: string;
  }

  const handleIntent = async (intendsToEnroll: boolean) => {
    try {
      const { academicPartnerUuid, uuid, programUuid } = application;
      const { enrollmentUuid, term: currentTerm, newTerm } = currentEnrollment;
      const expiredWithNewTerm = isExpired && newTerm;
      const {
        intends_to_enroll,
        max_enrollments_allowed,
        recorded_at,
        is_expired,
        term,
        enrollment_uuid,
      } = (await post({
        academic_partner_uuid: academicPartnerUuid || '',
        application_uuid: uuid,
        enrollment_uuid: enrollmentUuid,
        intends_to_enroll: intendsToEnroll,
        program_uuid: programUuid || '',
        term_code: expiredWithNewTerm ? newTerm!.code : currentTerm?.code || '',
        term_id: expiredWithNewTerm ? newTerm?.id : currentTerm?.id || '',
        start_date: expiredWithNewTerm ? newTerm?.startDate || '' : currentTerm?.startDate || '',
        end_date: expiredWithNewTerm ? newTerm?.endDate || '' : currentTerm?.endDate || '',
        user_uuid: user?.id ?? '',
      })) as EnrollmentServiceResponse;

      onSubmit({
        applicationId: application.id,
        enrollmentUuid: currentEnrollment.enrollmentUuid,
        enrollmentIntent: {
          intendsToEnroll: intends_to_enroll,
          maxEnrollmentsAllowed: max_enrollments_allowed,
          recordedAt: recorded_at,
          isExpired: is_expired,
          term: {
            name: term.name,
            code: term.code,
            startDate: term.start_date,
            endDate: term.end_date,
          },
          enrollmentUuid: enrollment_uuid,
        },
      });
      closeModal();
      if (enrollmentsRefetch) enrollmentsRefetch();
      openModal(
        <EnrollmentSuccessModal application={application} enrollmentIntent={intendsToEnroll} />
      );
    } catch (error) {
      setErrorMessage('There was an error saving your preference. Please try again later.');
    }
  };

  const primaryButtonConfig: DialogButton = {
    onClick: () => {
      handleSubmit(enrollmentIntent);
    },
    target: '_blank',
    text: 'Submit',
  };

  const secondaryButtonConfig: DialogButton = {
    appearance: 'outline',
    onClick: closeModal,
    target: '_blank',
    text: 'Not now',
  };

  const enrollmentModalTitle = `Confirm your spot in ${apName}'s ${application?.program.name}`;

  const enrollmentModalOptions: ModalOptions = {
    primaryButton: primaryButtonConfig,
    secondaryButton: secondaryButtonConfig,
    closable: true,
    describedBy: 'enrollment-modal-description',
    labelledBy: 'enrollment-modal-title',
    tag: 'enrollment_modal',
    containerClass: 'enrollmentModal',
  };

  useEffect(() => {
    setModalOptions(enrollmentModalOptions);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enrollmentIntent]);

  return (
    <div className='enrollmentModal'>
      <div className='enrollmentModalHeader'>
        <Typography
          id='enrollment-modal-title'
          variant='h3'
          gutterBottom={isMobile ? 'md' : undefined}
        >
          {enrollmentModalTitle}
        </Typography>
        {logo && <img className='enrollmentModalLogo' alt={`${apName} logo`} src={logo} />}
      </div>
      <div>
        {rolloverConditions ? (
          <div>
            <Typography variant='body1' gutterBottom='sm'>
              The deadline to confirm your spot for the {formattedStartDate} term has passed. But
              you can still enroll for the next available start date.
            </Typography>
            <Typography variant='body1' gutterBottom='sm'>
              Let us know if you would like to confirm your spot for the next available term and
              your school will contact you with next steps.
            </Typography>
          </div>
        ) : (
          <Typography variant='body1' gutterBottom='sm'>
            Let us know if you plan to enroll in this program for the coming term. Then your school
            will contact you with next steps. Be sure to confirm by {formattedStartDate}, or
            you&apos;ll have to start in a later term.
          </Typography>
        )}
      </div>
      <RadioButtons
        errors={errorMessage}
        groupLabel=''
        name='ITE'
        onChange={(event) => {
          setEnrollmentIntent(event.target.value);
        }}
        selectedValue={enrollmentIntent}
        values={
          [
            {
              label: (
                <Typography variant='body1' className='radioOptions'>
                  Yes, I&apos;d like to confirm my spot in this program for the term starting
                  <>
                    {' '}
                    <span className='modalStartDate'>
                      {rolloverConditions ? newTermFormattedStartDate : formattedStartDate}
                    </span>
                  </>
                  .
                </Typography>
              ),
              value: 'true',
            },
            {
              label: (
                <Typography variant='body1' className='radioOptions'>
                  No, I&apos;m no longer interested in this program.
                </Typography>
              ),
              value: 'false',
            },
          ] as any
        }
      />
    </div>
  );
};

export default EnrollmentConfirmationModal;
